import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileDown, MessageSquare, Shield } from "lucide-react";

import type { DiagnosticSnapshot } from "@/api/diagnostic";
import { CompactHero } from "@/components/brand/CompactHero";
import { HeroMetaItem, HeroMetaSep } from "@/components/brand/Hero";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useDiagnostic } from "@/hooks/useDiagnostic";
import { useCurrentUser } from "@/hooks/useAuth";
import { hasMinRole } from "@/lib/roles";
import { useProject } from "@/hooks/useApi";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import {
} from "@/components/projects/diagnostic/diagnosticData";
import { EnvironmentAct } from "@/components/projects/diagnostic/acts/EnvironmentAct";
import { DiagnosisAct } from "@/components/projects/diagnostic/acts/DiagnosisAct";
import { InvestmentAct } from "@/components/projects/diagnostic/acts/InvestmentAct";
import { TreatmentAct } from "@/components/projects/diagnostic/acts/TreatmentAct";
import {
  FINAL_ACT,
  FIRST_ACT,
  VISIBLE_ACT_IDS,
  actNumber,
  type ActId,
  type DiagnosticTab,
  SUMMARY_TAB,
} from "@/components/projects/diagnostic/acts";
import { ActFooterNav } from "@/components/projects/diagnostic/ActFooterNav";
import { DiagnosticDetailLayout } from "@/components/projects/diagnostic/DiagnosticDetailLayout";
import { DIAGNOSTIC_DETAILS } from "@/components/projects/diagnostic/details";
import { ActOpening } from "@/components/projects/diagnostic/ActOpening";
import { ActStepper } from "@/components/projects/diagnostic/ActStepper";
import { SummaryTab } from "@/components/projects/diagnostic/summary/SummaryTab";
import { PresenterNotes } from "@/components/projects/diagnostic/PresenterNotes";
import {
  FloatingChat,
  type ChatMode,
} from "@/components/agents/functional/patternC/FloatingChat";
import { formatDate } from "@/lib/datetime";
import { formatCount } from "@/lib/format";
import { PROJECT_MODE_LABEL, SOURCE_TYPE_LABEL, type SourceType } from "@/lib/projectSource";
import type { Project } from "@/types/api";
import { ProjectAreasProvider } from "@/components/projects/diagnostic/projectAreas";

const count = (n: number) => formatCount(n);

/**
 * A rota do Diagnóstico.
 *
 * O `key={projectId}` não é detalhe: sem ele, navegar de `/projects/A/diagnostic`
 * para `/projects/B/diagnostic` **não remonta** a tela. A rota é a mesma, muda só
 * o param, e o `ProjectRouteGuard` só segura os filhos enquanto `isLoading` for
 * verdadeiro; com o projeto de destino já no cache do TanStack (`staleTime` de
 * 30s) ele renderiza síncrono.
 *
 * O estado do roteiro sobreviveria à troca, e o efeito é o pior possível numa
 * ferramenta de venda: o Ato 5 do cliente B abrindo já revelado, com os números
 * de B, sem ninguém ter feito o gesto de revelar.
 *
 * A chave mora aqui, e não no `App.tsx`, porque o `projectId` já está em escopo
 * neste arquivo e porque quem precisa da garantia é esta tela.
 */
export default function DiagnosticRoute() {
  const { projectId } = useParams<{ projectId: string }>();
  return <ProjectDiagnostic key={projectId ?? "sem-projeto"} />;
}

/**
 * Tela de Diagnóstico da migração — **somente leitura**.
 *
 * Único componente da feature que chama `useDiagnostic`: os cinco atos e as
 * telas de detalhe recebem `sections` por props. O ato vive em memória e não vai
 * para a URL (#852); o que vai é o detalhe aberto, em `/diagnostic/<slug>`
 * (#1339).
 */
function ProjectDiagnostic() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectId, "*": rest } = useParams<{ projectId: string; "*": string }>();
  // O detalhe é rota (#1339), e o ato não: a rota é a mesma `/diagnostic/*`, então
  // abrir e fechar um detalhe não remonta a tela e o roteiro abaixo continua.
  // O registro é lido aqui, na renderização, para que cada EF só acrescente a linha.
  // A barra no fim sai antes da busca: link colado às vezes chega com ela.
  const detailSlug = rest?.replace(/\/+$/, "");
  const detail = DIAGNOSTIC_DETAILS.find((entry) => entry.slug === detailSlug);
  const { data, isLoading, isError } = useDiagnostic(projectId);
  // O nome do cliente vem de outra query, e é o título do artefato que abre a
  // reunião. Enquanto ela não responde, o rótulo genérico segura o lugar.
  const { data: project } = useProject(projectId);
  // O ato vive em memória, e não na URL (#852). Deep-link e a trava do Ato 5 não
  // convivem: recarregar com `?act=5` revelaria o número antes da hora, e colar
  // o link a alguém entregaria o final da apresentação.
  const [act, setAct] = useState<DiagnosticTab>(FIRST_ACT);

  // A revelação é irreversível **na sessão**, e não persistida. Voltar ao Ato 1
  // não refecha o Ato 5, porque quem já viu o número não desvê; mas abrir a tela
  // para o próximo cliente começa de novo, que é o que faz a devolutiva ter
  // clímax em vez de memória.
  const [revealed, setRevealed] = useState(false);

  // **Desligadas por default**, e isso diverge do desenho de propósito.
  //
  // O desenho parte de que quem abre a tela é quem apresenta. Só que o modo de
  // falha não é simétrico: desligar depois de projetar não desprojeta. O caminho
  // concreto é a pessoa já estar com a tela compartilhada e navegar para cá, e
  // no instante em que a tela pinta aparece o texto de coaching interno ("Nobody
  // leaves the room before Act 5", "the calculation sheet stays with Exed").
  //
  // O cabeçalho do bloco promete que as notas não entram no PDF do cliente, o
  // que resolve o anexo e não resolve a projeção. Quem esquece de ligar perde
  // uma dica; quem esquece de desligar mostra a cozinha. Os dois erros não
  // custam o mesmo.
  const [notes, setNotes] = useState(false);

  function goToAct(next: string) {
    // O resumo não é ato: abre sem passar pela trava do Ato 5.
    if (next === SUMMARY_TAB) return setAct(SUMMARY_TAB);
    if (!(VISIBLE_ACT_IDS as readonly string[]).includes(next)) return;
    const id = next as ActId;
    if (id === FINAL_ACT) setRevealed(true);
    setAct(id);
  }

  // A seta de um bloco do Resumo abre o ato e volta ao topo da página: quem clicou
  // estava no fim do Resumo, e o ato abriria na mesma altura de rolagem.
  const pageTopRef = useRef<HTMLDivElement>(null);

  function goToActFromSummary(next: ActId) {
    goToAct(next);
    pageTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Quem abriu o detalhe volta para a célula de onde saiu (critério da
  // EF-DIAG-002). Os atos desmontam enquanto o detalhe está aberto, então não há
  // elemento para guardar: guarda-se o slug, e a célula é o link que aponta para ele.
  const actsRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<string | null>(null);

  // Se o detalhe foi aberto daqui, a entrada anterior do histórico é a própria
  // devolutiva, e voltar é desempilhar. Empilhar a devolutiva de novo deixava o
  // detalhe no meio, e o voltar do navegador o reabria. Quem chegou por link
  // direto não tem a devolutiva atrás: aí o detalhe é trocado por ela.
  const openedFromActs = useRef(false);
  const hadDetail = useRef<boolean | null>(null);

  useEffect(() => {
    if (detail && hadDetail.current === false) openedFromActs.current = true;
    if (!detail) openedFromActs.current = false;
    hadDetail.current = Boolean(detail);
  }, [detail]);

  useEffect(() => {
    if (detail || !returnFocusTo.current) return;
    const origin = actsRef.current?.querySelector<HTMLElement>(
      `a[href$="/diagnostic/${returnFocusTo.current}"]`,
    );
    origin?.focus();
    returnFocusTo.current = null;
  }, [detail]);

  function closeDetail(from: NonNullable<typeof detail>) {
    returnFocusTo.current = from.slug;
    // Volta ao ato de origem sem passar pelo `goToAct`: voltar não é o gesto de
    // revelar. E um detalhe do Ato 5 ainda trancado não volta para o Ato 5, cujo
    // conteúdo é o número; fica no ato em que a pessoa estava.
    const lockedFinal = from.originAct === FINAL_ACT && !revealed;
    if (!lockedFinal) setAct(from.originAct);
    // ⚠️ Não cobre detalhe → detalhe. De A para B o `hadDetail` já é `true`, o
    // `openedFromActs` fica grudado no `true` de A, e o `-1` volta para A em vez
    // da devolutiva. Hoje nenhum detalhe linka outro; a EF que criar o primeiro
    // link entre detalhes precisa desempilhar quantos detalhes houver.
    if (openedFromActs.current) navigate(-1);
    else navigate(`/projects/${projectId}/diagnostic`, { replace: true });
  }

  const sections = data?.sections ?? {};
  const snapshots = data?.snapshots;
  const tabProps = {
    sections,
    snapshots,
    calibration: data?.calibration,
    validation: data?.validation,
  };

  // #689: o chat sai do workbench de execução e ganha superfície aqui, que é a
  // tela que o comercial apresenta. Sem `executionId`: a conversa é estável por
  // (projeto, agente) e o contexto junta a última run do Functional e do
  // Architect, porque esta tela mistura os dois e quem pergunta na sala não
  // sabe (nem precisa saber) qual agente é dono de qual resposta.
  const [chatMode, setChatMode] = useState<ChatMode>("closed");
  const hasAnyRun = Boolean(
    snapshots?.functional?.available || snapshots?.architect?.available,
  );

  return (
    <div className="-m-6">
      <div ref={pageTopRef} data-testid="diagnostic-hero">
        <CompactHero
          glyph={<Shield />}
          overline={t("DIAGNOSTIC DEBRIEF")}
          title={project?.name ?? t("Diagnostic")}
          miniBadge={
            <span data-testid="diagnostic-act-pill">
              {act === SUMMARY_TAB && !detail
                ? t("Debrief summary")
                : t("Act {{current}} of {{total}}", {
                    // Com detalhe aberto, o ato é o dele: por link direto o ato em
                    // memória é o primeiro, e a pílula contradiria o breadcrumb.
                    current: actNumber(detail?.originAct ?? (act as ActId)),
                    total: VISIBLE_ACT_IDS.length,
                  })}
            </span>
          }
          meta={<SnapshotBar snapshots={snapshots} loading={isLoading} project={project} />}
          actions={
            hasAnyRun && projectId ? (
              <HeroActions
                projectId={projectId}
                onOpenChat={() => setChatMode("floating")}
              />
            ) : undefined
          }
        />
      </div>

      {/*
        Slug fora do registro abre a devolutiva, e a URL é trocada pela dela: não
        fica dizendo um endereço que a tela não mostra, nem no histórico.
      */}
      {detailSlug && !detail && (
        <Navigate to={`/projects/${projectId}/diagnostic`} replace />
      )}

      {detail ? (
        <DiagnosticDetailLayout
          detail={detail}
          revealed={revealed}
          onBack={() => closeDetail(detail)}
          {...tabProps}
          snapshotState={isLoading ? "loading" : !data && isError ? "failed" : undefined}
        />
      ) : (
        /*
          `activationMode="manual"` não é preferência de estilo. O default do Radix
          é `automatic` com `loop` ligado: uma seta para a esquerda a partir do
          Ato 1 põe foco no Ato 5 e o ativa pelo `onFocus`, revelando o número sem
          ninguém ter pedido. Com `manual`, a seta move o foco e só Enter ou Espaço
          abrem.
        */
        <Tabs ref={actsRef} value={act} onValueChange={goToAct} activationMode="manual">
          <ActStepper
            act={act}
            revealed={revealed}
            notes={notes}
            onToggleNotes={() => setNotes((v) => !v)}
          />

          {/* #1784: as áreas que a origem do projeto não tem somem dos atos. */}
          <ProjectAreasProvider unavailable={project?.unavailable_areas}>
            <div className="p-8">
              <TabsContent value={SUMMARY_TAB}>
                <SummaryTab
                  sections={sections}
                  validation={data?.validation}
                  calibration={data?.calibration}
                  snapshotState={isLoading ? "loading" : !data && isError ? "failed" : undefined}
                  clientName={project?.name}
                  onGoToAct={goToActFromSummary}
                />
              </TabsContent>
              <TabsContent value="environment">
                <ActOpening act="environment" />
                <EnvironmentAct {...tabProps} />
                {notes && <PresenterNotes act="environment" />}
                <ActFooterNav act="environment" onGo={goToAct} />
              </TabsContent>
              <TabsContent value="diagnosis">
                <ActOpening act="diagnosis" />
                <DiagnosisAct {...tabProps} />
                {notes && <PresenterNotes act="diagnosis" />}
                <ActFooterNav act="diagnosis" onGo={goToAct} />
              </TabsContent>
              <TabsContent value="treatment">
                <ActOpening act="treatment" />
                <TreatmentAct {...tabProps} />
                {notes && <PresenterNotes act="treatment" />}
                <ActFooterNav act="treatment" onGo={goToAct} />
              </TabsContent>
              <TabsContent value="investment">
                <ActOpening act="investment" />
                <InvestmentAct {...tabProps} />
                {notes && <PresenterNotes act="investment" />}
                <ActFooterNav act="investment" onGo={goToAct} />
              </TabsContent>
            </div>
          </ProjectAreasProvider>
        </Tabs>
      )}

      {/*
        Montado só depois de aberto. Com `mode === "closed"` o `FloatingChat`
        desenha o próprio pill dourado no canto, e com o botão do hero ficariam
        dois gatilhos para a mesma coisa.
      */}
      {hasAnyRun && projectId && chatMode !== "closed" && (
        <div data-testid="diagnostic-chat">
          <FloatingChat
            mode={chatMode}
            setMode={setChatMode}
            projectId={projectId}
            agentId="diagnostic"
            agentLabel={t("Diagnostic")}
            isComplete
          />
        </div>
      )}
    </div>
  );
}

/**
 * As duas ações do hero: montar o PDF do cliente e abrir o chat (#852).
 *
 * #1772: "Exportar PDF" leva à tela de montagem do PDF do cliente (`ClientPdfPage`,
 * em `/diagnostic/pdf`, na mesma aba). O
 * PDF técnico (`/diagnostic/download`) ficou sem botão, só interno pelo endereço
 * (decisão 13 da #1770). O texto abaixo conta a história do botão antigo.
 *
 * O desenho aprovado põe dois botões aqui, "Exportar PDF" e "Apresentar". O modo
 * apresentação foi abandonado, e o segundo slot passou a ser o chat, a pedido do
 * Casado. O rótulo diz o que o botão faz: "Apresentar" abrindo um chat seria a
 * única mentira da tela.
 *
 * O DOCX saiu. O desenho tem um botão só, e o comercial que quer editar antes de
 * mandar continua tendo o `?format=docx` no endpoint; o que sai é a superfície,
 * não o formato.
 *
 * Link direto, e não `fetch` com blob: o navegador já sabe salvar arquivo a
 * partir do `Content-Disposition`, e o cookie de sessão viaja sozinho.
 *
 * ⚠️ O arquivo sai carimbado como uso interno, e isso é deliberado: a dúvida 3
 * do plano de pré-venda ("até onde o cliente enxerga") não foi ratificada, e é
 * ela que define o que fica retido numa versão de cliente.
 */
function HeroActions({
  projectId,
  onOpenChat,
}: {
  projectId: string;
  onOpenChat: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // O PDF do cliente é gerado do comercial para cima (#1771); para quem não gera,
  // o botão não aparece em vez de levar a uma tela que o recusa.
  const canExport = hasMinRole(useCurrentUser()?.role, "sales");
  const actionClass =
    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors";

  return (
    <div className="flex items-center gap-2">
      {canExport && (
        <button
          type="button"
          data-testid="diagnostic-export-pdf"
          onClick={() => navigate(`/projects/${projectId}/diagnostic/pdf`)}
          className={`${actionClass} border border-white/25 text-white/90 hover:bg-white/10`}
        >
          <FileDown aria-hidden className="h-3.5 w-3.5" />
          {t("Export PDF")}
        </button>
      )}
      <button
        type="button"
        data-testid="diagnostic-chat-open"
        onClick={onOpenChat}
        className={`${actionClass} bg-[#ffc83d] text-[#08254c] hover:bg-[#ffb300]`}
      >
        <MessageSquare aria-hidden className="h-3.5 w-3.5" />
        {t("Ask myGROW")}
      </button>
    </div>
  );
}

/** Barra de snapshot: qual run, qual varredura, quando. */
/** Nome de produto do modelo de ERP. Token desconhecido volta como veio. */
const ERP_MODEL_LABEL: Record<string, string> = {
  ecc: "SAP ECC",
  s4: "SAP S/4HANA",
  business_one: "SAP Business One",
};

/**
 * `SAP ECC · ABA/800 · rel. 757`, com o que a origem tiver devolvido.
 *
 * O `test_connection` nem sempre expõe os quatro atributos, e a regra do nó 01 é
 * não inventar: campo nulo simplesmente não entra na linha. Sem nenhum deles a
 * linha vira `—`, que é o token de ausência do resto da tela — e não some, porque
 * "não sei de que sistema isto saiu" é resposta, e linha ausente não é.
 */
function sourceSystemLabel(
  system: DiagnosticSnapshot["snapshots"]["functional"]["system_info"],
  t: (key: string) => string,
  declared?: SourceType,
): string {
  // #1789: a identidade vem da carga vigente do ex-kit. Sem carga não há de onde tirá-la,
  // e a linha diz isso, em vez do travessão de "o sistema não respondeu". Desde a #1784 o
  // projeto declara a origem, e ela vai na frente.
  if (!system) {
    const missing = t("No extraction kit load");
    return declared ? `${t(SOURCE_TYPE_LABEL[declared])} · ${missing}` : missing;
  }
  const parts: string[] = [];
  if (system.erp_system_model) {
    const produto = ERP_MODEL_LABEL[system.erp_system_model] ?? system.erp_system_model;
    // #939: o desenho escreve "Origem SAP ECC 6.0 EhP7", e o nível do
    // `SAP_APPL` agora chega pela `CVERS`. O "6.0" acompanha o EhP porque todo
    // enhancement package de 1 a 8 é sobre o ERP 6.0 — sem EhP não há o que o
    // sustente, e um S/4HANA cairia nesse caso.
    parts.push(
      system.enhancement_package
        ? `${produto} 6.0 ${system.enhancement_package}`
        : system.s4_release
          ? `${produto} ${system.s4_release}`
          : produto,
    );
  }
  if (system.sid) parts.push(system.client ? `${system.sid}/${system.client}` : system.sid);
  if (system.release) parts.push(`${t("rel.")} ${system.release}`);
  return parts.length > 0 ? parts.join(" · ") : NO_VALUE;
}

function SnapshotBar({
  snapshots,
  loading,
  project,
}: {
  snapshots?: DiagnosticSnapshot["snapshots"];
  loading: boolean;
  project?: Pick<Project, "source_type" | "mode">;
}) {
  const { t } = useTranslation();

  if (loading) return <HeroMetaItem k={t("Snapshot")} v={t("Loading…")} />;

  const functional = snapshots?.functional;
  const architect = snapshots?.architect;

  // #830: a run mais recente pode ser a pior. O projeto `43cfcdf0` tem quatro
  // execuções concluídas do Functional; a escolhida traz 252 chars de
  // `output_summary` e as três anteriores, 21 a 22 MB. A escolha do resolvedor
  // continua sendo a mais recente (é a sem surpresa), mas calar sobre as outras
  // faz um projeto com dado parecer vazio na tela que o comercial apresenta.
  //
  // Soma dos dois agentes de propósito: o aviso diz "há mais de onde isto veio",
  // e quem precisa saber de qual agente abre o histórico de execuções.
  const otherRuns =
    (functional?.other_completed_with_content ?? 0) +
    (architect?.other_completed_with_content ?? 0);

  return (
    <span data-testid="diagnostic-context">
      <HeroMetaItem
        k={t("Origin")}
        v={sourceSystemLabel(functional?.system_info, t, project?.source_type)}
      />
      <HeroMetaSep />
      {project?.mode && (
        <>
          <HeroMetaItem k={t("Mode")} v={t(PROJECT_MODE_LABEL[project.mode])} />
          <HeroMetaSep />
        </>
      )}
      <HeroMetaItem
        k={t("Collected")}
        v={functional?.completed_at ? formatDate(functional.completed_at) : NO_VALUE}
      />
      <HeroMetaSep />
      {/*
        Os ids saem da linha, não do produto. Rastrear uma run é trabalho real de
        quem opera a ferramenta; o que muda é a hierarquia, porque o cliente lê
        este cabeçalho antes de qualquer outra coisa e dois UUIDs não dizem nada
        a ele. O `title` é onde quem precisa copiar já procura.
      */}
      <span
        data-testid="diagnostic-run-ids"
        className="cursor-help text-white/40"
        title={[
          `${t("Functional run")}: ${functional?.execution_id ?? NO_VALUE}`,
          `${t("Architect scan")}: ${architect?.scan_job_id ?? architect?.execution_id ?? NO_VALUE}`,
        ].join("\n")}
      >
        {t("Runs")}
      </span>
      {otherRuns > 0 && (
        <>
          <HeroMetaSep />
          <span
            data-testid="diagnostic-other-runs"
            title={t(
              "The diagnostic shows the most recent completed run. Older runs with data are still in the execution history.",
            )}
          >
            <span className="mr-1 text-white/40">{t("Other runs with data")}</span>
            <strong className="font-medium text-[#ffc83d]">{count(otherRuns)}</strong>
          </span>
        </>
      )}
    </span>
  );
}
